<script setup lang="ts">
import LeIconButton from "@dsk/components/IconButton/LeIconButton.vue";
import LeMinus from "@dsk/icons/LeMinus.vue";
import LePlus from "@dsk/icons/LePlus.vue";
import { storeToRefs } from "pinia";
import { computed, ref, watch } from "vue";
import { useI18n } from "vue-i18n";
import { RouterLink, useRouter } from "vue-router";

import LeDictionaryCover from "@/components/dictionaries/LeDictionaryCover.vue";
import { SearchRouteHelper } from "@/helpers";
import { useArticleFeedbackModalStore, useUserStore } from "@/stores";
import type { ArticleFormat, Dictionary, DictionaryMethod, JsonArticle, SearchViewType } from "@/types";
import type { JsonArticleComponent } from "@/types/components/json-article-component.type";

import LeClausensArticle from "./clausens/LeArticle.vue";
import LeDictsysArticle from "./dictsys/LeArticle.vue";
import LeAboutDictionaryButton from "./LeAboutDictionaryButton.vue";
import LeArticleFeedbackButton from "./LeArticleFeedbackButton.vue";
import LeCompactFooter from "./LeCompactFooter.vue";

const { article, dictionary, format, method, searchViewType } = defineProps<{
  article: JsonArticle;
  dictionary?: Dictionary;
  format: ArticleFormat;
  method?: DictionaryMethod;
  searchViewType: SearchViewType;
}>();

const { t } = useI18n();

const { openFeedbackModal } = useArticleFeedbackModalStore();
const { userData } = storeToRefs(useUserStore());

const ArticleComponent = computed<JsonArticleComponent>(() => {
  if (format === "dictsys") {
    return LeDictsysArticle;
  }

  if (format === "clausens") {
    return LeClausensArticle;
  }

  throw new Error(`Unknown article format: ${format}`);
});

const searchRouteHelper = new SearchRouteHelper(useRouter(), userData);

const onOpenFeedbackModal = (jsonArticle: JsonArticle) => {
  if (!dictionary) {
    return;
  }

  openFeedbackModal(jsonArticle, dictionary, jsonArticle.methodId);
};

const expandCompactArticlesByDefault = computed<boolean>(() => userData.value.settings.compactArticlesExpanded);
const isExpanded = ref<boolean>(expandCompactArticlesByDefault.value);
watch(expandCompactArticlesByDefault, (value) => {
  isExpanded.value = value;
});
</script>

<template>
  <ArticleComponent :article="article" :is-compact-article-expanded="isExpanded" :search-view-type="searchViewType">
    <template #dictionaryCover>
      <LeDictionaryCover v-if="dictionary" :dictionary="dictionary" size="large" with-tooltip with-subtitle />
    </template>
    <template #link="{ word }">
      <RouterLink :to="searchRouteHelper.generateQuerySearchUrl(word)">{{ word }}</RouterLink>
    </template>
    <template #feedbackButton>
      <LeArticleFeedbackButton
        :tooltip="t('le-dictionary-feedback.button-tooltip.text')"
        @click="onOpenFeedbackModal(article)"
      />
    </template>
    <template #aboutDictionaryButton>
      <LeAboutDictionaryButton
        v-if="dictionary"
        :dictionary="dictionary"
        :tooltip="t('le-dictionary-info-button.tooltip')"
      />
    </template>
    <template #compactExpandButton>
      <LeIconButton
        class="le-json-article__expand-toggle"
        :class="{ 'le-json-article__expand-toggle--expanded': isExpanded }"
        color="primary"
        size="l"
        @click="isExpanded = !isExpanded"
      >
        <LeMinus v-if="isExpanded" />
        <LePlus v-else />
      </LeIconButton>
    </template>
    <template #compactFooter>
      <LeCompactFooter v-if="dictionary && method" :dictionary="dictionary" :method="method" />
    </template>
  </ArticleComponent>
</template>

<style lang="less" scoped>
.le-json-article {
  &__expand-toggle {
    display: inline;
    margin-right: 16px;

    &--expanded {
      margin-bottom: 14px;
    }
  }
}
</style>
